import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { fetchInteractionSummary } from '../../api/userInteractionReports';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#8DD1E1'];

/**
 * Component for displaying interaction summary
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 */
function InteractionSummary({ filters }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  // Load interaction summary when filters change
  useEffect(() => {
    const loadSummary = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchInteractionSummary(filters);
        setSummary(data);
      } catch (error) {
        console.error('Error loading interaction summary:', error);
        setError('Failed to load interaction summary. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, [filters]);

  // If loading, show loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // If no data, show message
  if (!summary || summary.totalInteractions === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No interaction data available for the selected filters.
      </Alert>
    );
  }

  // Prepare data for charts
  const interactionTypeData = Object.entries(summary.interactionsByType || {})
    .map(([type, count]) => ({ name: type, value: count }));
  
  const componentData = Object.entries(summary.interactionsByComponent || {})
    .map(([component, count]) => ({ name: component, value: count }));

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Interaction Summary
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {/* Total Interactions Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Interactions
            </Typography>
            <Typography variant="h4">
              {summary.totalInteractions}
            </Typography>
          </CardContent>
        </Card>
        
        {/* Most Common Interaction Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Most Common Interaction
            </Typography>
            <Typography variant="h5">
              {summary.mostCommonInteractions?.[0]?.type || 'N/A'}
            </Typography>
            <Typography variant="body2">
              {summary.mostCommonInteractions?.[0]?.count || 0} occurrences
            </Typography>
          </CardContent>
        </Card>
        
        {/* Most Active Component Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Most Active Component
            </Typography>
            <Typography variant="h5">
              {summary.mostActiveComponents?.[0]?.component || 'N/A'}
            </Typography>
            <Typography variant="body2">
              {summary.mostActiveComponents?.[0]?.count || 0} interactions
            </Typography>
          </CardContent>
        </Card>
        
        {/* Most Active User Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Most Active User
            </Typography>
            <Typography variant="h5">
              {summary.mostActiveUsers?.[0]?.userId || 'N/A'}
            </Typography>
            <Typography variant="body2">
              {summary.mostActiveUsers?.[0]?.count || 0} interactions
            </Typography>
          </CardContent>
        </Card>
      </Box>
      
      <Grid container spacing={3}>
        {/* Interactions by Day Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Interactions by Day
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={summary.interactionsByDayArray || []}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#8884d8" name="Interactions" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Interactions by Type Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              Interactions by Type
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={interactionTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {interactionTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Interactions by Component Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              Interactions by Component
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={componentData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {componentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        
        {/* Most Common Interactions Table */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Most Common Interactions
            </Typography>
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="most common interactions table">
                <TableHead>
                  <TableRow>
                    <TableCell>Interaction Type</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(summary.mostCommonInteractions || []).map((interaction) => (
                    <TableRow
                      key={interaction.type}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {interaction.type}
                      </TableCell>
                      <TableCell align="right">{interaction.count}</TableCell>
                      <TableCell align="right">
                        {((interaction.count / summary.totalInteractions) * 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        
        {/* Most Active Components Table */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Most Active Components
            </Typography>
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="most active components table">
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell align="right">Interactions</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(summary.mostActiveComponents || []).map((component) => (
                    <TableRow
                      key={component.component}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {component.component}
                      </TableCell>
                      <TableCell align="right">{component.count}</TableCell>
                      <TableCell align="right">
                        {((component.count / summary.totalInteractions) * 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default InteractionSummary;
